import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { useSuspenseQueries } from '@tanstack/react-query'
import { useConfig } from 'wagmi'

import { predefinedDelegates } from '../config'
import { delegateQueryFunction, delegateQueryKey } from '../dialogs/delegate/logic/delegateQuery'
import { Proposal } from '../types'
import { governanceProposalsQueryFn, governanceProposalsQueryKey } from './governanceProposalsQuery'
import { snapshotVotingPowerQueryFn, snapshotVotingPowerQueryKey } from './snapshotVotingPower'
import { spkSlashableBalanceQueryFunction, spkSlashableBalanceQueryKey } from './spkSlashableBalanceQuery'
import { Token } from '@/domain/types/Token'

export interface UseSpkGovernanceDataParams {
  account: CheckedAddress | undefined
  chainId: number
  spk: Token
  spkBalance: NormalizedNumber
}

export interface UseSpkGovernanceDataResult {
  proposals: Proposal[]
  votingPower: NormalizedNumber
  delegate: CheckedAddress | undefined
  slashableStakedSpk: NormalizedNumber
}

export function useSpkGovernanceData({
  account,
  chainId,
  spk,
  spkBalance,
}: UseSpkGovernanceDataParams): UseSpkGovernanceDataResult {
  const wagmiConfig = useConfig()
  const userIsDelegate = predefinedDelegates.some(({ address }) => address === account)

  const [proposals, snapshotVotingPower, delegate, slashableStakedSpk] = useSuspenseQueries({
    queries: [
      {
        queryKey: governanceProposalsQueryKey(),
        queryFn: governanceProposalsQueryFn,
      },
      {
        queryKey: snapshotVotingPowerQueryKey({ account }),
        queryFn: userIsDelegate ? () => snapshotVotingPowerQueryFn({ account }) : () => null,
      },
      {
        queryKey: delegateQueryKey({ delegator: account ?? CheckedAddress.ZERO(), chainId }),
        queryFn: account ? () => delegateQueryFunction({ delegator: account, chainId, wagmiConfig }) : () => null,
      },
      {
        queryKey: spkSlashableBalanceQueryKey({ address: account, chainId }),
        queryFn: () => spkSlashableBalanceQueryFunction({ address: account, chainId, wagmiConfig, spk }),
      },
    ],
  })

  const votingPowerWithoutDelegations = spkBalance.plus(slashableStakedSpk.data)

  return {
    proposals: proposals.data,
    votingPower: snapshotVotingPower.data?.votingPower ?? votingPowerWithoutDelegations,
    delegate: delegate.data?.delegate,
    slashableStakedSpk: slashableStakedSpk.data,
  }
}
